import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";
import defaultAvatar from "../assets/default-avatar.png";
import SignUpModal from "../components/SignUpModal";
import SignInModal from "../components/SignInModal";
import "../styles/Navbar.css";
import { UserContext } from "../UserContext";
import ThemeToggle from "../theme/ThemeToggle.jsx";
import { useTranslation } from "react-i18next";

function Navbar() {
  const [showSignup, setShowSignup] = useState(false);
  const [showSignin, setShowSignin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const { profilePic, updateProfilePic } = useContext(UserContext);
  const [currentAvatar, setCurrentAvatar] = useState(defaultAvatar);
  const { t } = useTranslation();

  useEffect(() => {
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    const storedPic = localStorage.getItem("userPic");

    setIsLoggedIn(loggedIn);

    if (storedPic) {
      updateProfilePic(storedPic);
      setCurrentAvatar(storedPic);
    } else {
      updateProfilePic(storedPic || "");
      setCurrentAvatar(storedPic || defaultAvatar);
    }
  }, [updateProfilePic]);

  useEffect(() => {
    if (profilePic) {
      setCurrentAvatar(profilePic);
    } else {
      const storedPic = localStorage.getItem("userPic");
      if (storedPic && storedPic.startsWith("http")) {
        setCurrentAvatar(storedPic);
      } else {
        setCurrentAvatar(defaultAvatar);
      }
    }
  }, [profilePic]);

  // ✅ Listen for profile changes (e.g. updated image after editing)
  useEffect(() => {
    const refreshProfile = () => {
      const newPic = localStorage.getItem("userPic");
      if (newPic) {
        updateProfilePic(newPic);
        setCurrentAvatar(newPic);
      } else {
        updateProfilePic("");
        setCurrentAvatar(defaultAvatar);
      }
    };

    window.addEventListener("profile-change", refreshProfile);
    return () => window.removeEventListener("profile-change", refreshProfile);
  }, [updateProfilePic]);

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setShowDropdown(false);
    updateProfilePic("");
    setCurrentAvatar(defaultAvatar);
  };

  const handleSignupSuccess = () => {
    const storedPic = localStorage.getItem("userPic");

    setIsLoggedIn(true);

    if (storedPic && storedPic.startsWith("http")) {
      updateProfilePic(storedPic);
      setCurrentAvatar(storedPic);
    } else {
      updateProfilePic("");
      setCurrentAvatar(defaultAvatar);
    }

    setShowSignup(false);
  };

  const handleSigninSuccess = () => {
    const storedPic = localStorage.getItem("userPic");

    setIsLoggedIn(true);

    if (storedPic && storedPic.startsWith("http")) {
      updateProfilePic(storedPic);
      setCurrentAvatar(storedPic);
    } else {
      updateProfilePic("");
      setCurrentAvatar(defaultAvatar);
    }

    setShowSignin(false);
  };

  return (
    <>
      <Link to="/" className="floating-logo-link" aria-label="Health W AI home">
        <img src={logo} alt="Health W AI" />
      </Link>
      <nav className="navbar">
        <div className="nav-left">
          <Link to="/" className="btn-outline mobile-home-btn">Home</Link>
        </div>

        <div className="nav-links" aria-label="Main navigation">
          <Link to="/">{t("mvp.nav_home")}</Link><Link to="/tool">{t("mvp.nav_check")}</Link><Link to="/wellness-guide">{t("mvp.nav_guide")}</Link><Link to="/about">{t("mvp.nav_mission")}</Link>
        </div>

        <div className="nav-actions">
          <div className="nav-lang-theme">
            <ThemeToggle />
          </div>

          {isLoggedIn ? (
            <div
              className="profile-wrapper"
              onClick={() => setShowDropdown((s) => !s)}
              role="button"
              tabIndex={0}
            >
              <img
                src={currentAvatar}
                alt="Profile"
                className="profile-icon"
                onError={(e) => (e.currentTarget.src = defaultAvatar)}
              />
              {showDropdown && (
                <div className="profile-dropdown">
                  <Link to="/profile" className="dropdown-item">My Profile</Link>
                  <button className="dropdown-item as-button" onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-buttons">
              <button className="btn-primary" onClick={() => setShowSignin(true)}>
                {t("sign_in")}
              </button>
              <button className="btn-outline" onClick={() => setShowSignup(true)}>
                {t("sign_up")}
              </button>
            </div>
          )}
        </div>
      </nav>

      {showSignup && (
        <SignUpModal onClose={() => setShowSignup(false)} onSignupSuccess={handleSignupSuccess} />
      )}
      {showSignin && (
        <SignInModal onClose={() => setShowSignin(false)} onSigninSuccess={handleSigninSuccess} />
      )}
    </>
  );
}

export default Navbar;
